import { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { PencilSquareIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import { showAlert } from 'services/alerts.service';
import { makeRequest } from 'services/request.service';
import TableComponent from 'components/common/TableComponent';
import ButtonComponent from 'components/common/ButtonComponent';
import ActionCardComponent from 'components/common/ActionCardComponent';
import DeviceGroupModal from 'components/modals/DeviceGroupModal';

const DevicesManagement = ({ devicesGroups, onRefreshDevicesGroups, user }) => {
  const { t } = useTranslation();
  const [state, setState] = useState({
    searchTermDeviceGroups: '',
    isOpenDeviceGroupModal: false,
    selectedDeviceGroupToEdit: null
  });

  const { isOpenDeviceGroupModal } = state;

  // Helper for auth headers
  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('secret')}`,
    'Content-Type': 'application/json'
  });

  // PUT/POST devices groups
  const handleCreateDeviceGroup = async data => {
    try {
      const isEditing = !!state?.selectedDeviceGroupToEdit?.deviceGroupId;

      const method = isEditing ? 'PUT' : 'POST';
      const url = isEditing
        ? `/management/devices/groups/${state.selectedDeviceGroupToEdit.deviceGroupId}`
        : `/management/devices/groups/${user.farmId}`;

      const response = await makeRequest({
        method,
        url,
        data,
        headers: getAuthHeaders(),
        alertResult: true
      });

      if (response.status === 200) {
        handleToggleDeviceGroupModal();
        await onRefreshDevicesGroups();
      }
    } catch (error) {
      showAlert({ message: t('devices.saveFailed'), icon: 'error' });
      console.error('Error saving device group:', error);
    }
  };

  // Delete device group (bottom table)
  const handleDeleteDeviceGroup = async deviceGroup => {
    try {
      const response = await makeRequest({
        method: 'DELETE',
        url: `/management/devices/groups/${deviceGroup.deviceGroupId}`,
        headers: getAuthHeaders(),
        alertResult: true
      });

      if (response.status === 200) {
        await onRefreshDevicesGroups();
        showAlert({ message: t('devices.deleteGroupSuccess'), icon: 'success' });
      }
    } catch (err) {
      console.error('Error deleting device group:', err);
      showAlert({ message: t('devices.deleteGroupFailed'), icon: 'error' });
    }
  };

  const handleToggleDeviceGroupModal = (deviceGroup = null) =>
    setState(s => ({
      ...s,
      isOpenDeviceGroupModal: !isOpenDeviceGroupModal,
      selectedDeviceGroupToEdit: deviceGroup
    }));

  // Filter device groups for bottom table
  const filteredDeviceGroups = devicesGroups.filter(group => {
    const searchTerm = state.searchTermDeviceGroups.toLowerCase();
    return (
      group.deviceGroupId.toString().includes(searchTerm) ||
      group.groupName.toLowerCase().includes(searchTerm)
    );
  });

  const devicesTableData = {
    columns: [
      { key: 'deviceGroupId', label: t('devices.deviceId') },
      { key: 'groupName', label: t('devices.groupName') },
      {
        key: 'actions',
        label: t('table.manage'),
        cell: (value, row) => (
          <div className="flex items-center gap-2">
            <button
              title={t('common.edit')}
              onClick={() => handleToggleDeviceGroupModal(row)}
              className="p-1 rounded transition"
            >
              <PencilSquareIcon className="w-5 h-5 text-blue-500" />
            </button>
            <button
              onClick={() => handleDeleteDeviceGroup(row)}
              title={t('devices.deleteDeviceGroup')}
              className="p-1 rounded transition"
            >
              <TrashIcon className="w-5 h-5 text-red-500" />
            </button>
          </div>
        )
      }
    ],
    rows: filteredDeviceGroups
  };

  return (
    <>
      {/* Device Groups Management */}
      <ActionCardComponent>
        <input
          type="text"
          placeholder={t('devices.searchDeviceGroups')}
          value={state.searchTermDeviceGroups}
          onChange={e =>
            setState(s => ({ ...s, searchTermDeviceGroups: e.target.value.toLowerCase() }))
          }
          className="search-input w-full mt-1 p-2 border rounded-md shadow-sm"
          data-align="left"
        />
        <ButtonComponent onClick={handleToggleDeviceGroupModal}>
          <PlusIcon className="w-5 h-5" />
          {t('devices.newDeviceGroup')}
        </ButtonComponent>
      </ActionCardComponent>

      <TableComponent
        tableData={devicesTableData}
        defaultSortColumn={devicesTableData.columns[0]?.key}
      />

      {/* Modals */}
      <DeviceGroupModal
        key={isOpenDeviceGroupModal ? 'open' : 'closed'}
        isOpen={isOpenDeviceGroupModal}
        handleSave={handleCreateDeviceGroup}
        handleClose={handleToggleDeviceGroupModal}
        selectedDevice={state.selectedDeviceGroupToEdit}
      />
    </>
  );
};

DevicesManagement.propTypes = {
  devicesGroups: PropTypes.arrayOf(PropTypes.object),
  onRefreshDevicesGroups: PropTypes.func,
  user: PropTypes.shape({ farmId: PropTypes.string })
};

export default DevicesManagement;
